<script setup>
import { ref } from 'vue'
import { mdiCalendarQuestion, mdiChevronUp, mdiChevronDown, mdiSimAlertOutline } from '@mdi/js'

import ColapsibleCard from '@/components/ColapsibleCard.vue'
import ValueCol from '@/components/ValueCol.vue'

const { eoir_case } = defineProps({ eoir_case: Object })

const selectedProceedingTab = ref([eoir_case.proceedings.length])

function scheduledHearingTypeDescription (code) {
  if (code == 'A') return 'Adjournment'
  if (code == 'B') return 'Bond'
  if (code == 'O') return 'Motion'
  if (code == 'X') return 'Proceeding'

  return `Unknown code ${code}`
}

function scheduleMediumDescription (code) {
  if (code == 'P') return 'In Person'
  if (code == 'T') return 'By Telephone'
  if (code == 'V') return 'By Video Link'
  if (code == 'A/W') return 'Internet/alt or Web'
  if (code == 'C') return 'Internet Court'
  if (code == 'H') return 'Hearing did not occur'

  return `Unknown code ${code}`
}
</script>

<template>
  <v-container>
    <h1 class="text-headline-large">
      Case Information
    </h1>
    <h2 class="text-headline-small">
      EOIR Case Id #{{ eoir_case.IDNCASE }}
    </h2>

    <v-row>
      <v-col cols="12">
        <ColapsibleCard title="Case Details">
          <v-container fluid>
            <v-row>
              <ValueCol label="Case Type">{{ eoir_case.case_type?.strDescription }}</ValueCol>
              <ValueCol label="Type of Asylum Claim">{{ eoir_case.C_ASY_TYPE }}</ValueCol>
              <ValueCol label="Case Priority">{{ eoir_case.case_priority?.strDescription }}</ValueCol>

              <ValueCol label="Nationality">{{ eoir_case.nationality.NAT_NAME }}</ValueCol>
              <ValueCol label="Language">{{ eoir_case.language.strDescription }}</ValueCol>
              <ValueCol label="Sex">{{ eoir_case.Sex }}</ValueCol>
              <ValueCol label="Birthdate">{{ eoir_case.C_BIRTHDATE }}</ValueCol>

              <ValueCol label="Date of Entry">{{ eoir_case.DATE_OF_ENTRY }}</ValueCol>
              <ValueCol label="Custody">{{ eoir_case.custody_status?.strDescription }}</ValueCol>
              <ValueCol label="Date of form E-28 filing">{{ eoir_case.E_28_DATE }}</ValueCol>

              <ValueCol label="ALIEN_CITY">{{ eoir_case.ALIEN_CITY }}</ValueCol>
              <ValueCol label="ALIEN_STATE">{{ eoir_case.ALIEN_STATE }}</ValueCol>
              <ValueCol label="ALIEN_ZIPCODE">{{ eoir_case.ALIEN_ZIPCODE }}</ValueCol>
              <ValueCol label="UPDATED_ZIPCODE">{{ eoir_case.UPDATED_ZIPCODE }}</ValueCol>
              <ValueCol label="UPDATED_CITY">{{ eoir_case.UPDATED_CITY }}</ValueCol>
              <ValueCol label="ADDRESS_CHANGEDON">{{ eoir_case.ADDRESS_CHANGEDON }}</ValueCol>

              <ValueCol label="SITE_TYPE">{{ eoir_case.SITE_TYPE }}</ValueCol>
              <ValueCol label="ATTY_NBR">{{ eoir_case.ATTY_NBR }}</ValueCol>
              <ValueCol label="UPDATE_SITE">{{ eoir_case.UPDATE_SITE }}</ValueCol>
              <ValueCol label="Latest Hearing Date">{{ eoir_case.LATEST_HEARING }} {{ eoir_case.LATEST_TIME }}</ValueCol>
              <ValueCol label="LATEST_CAL_TYPE">{{ eoir_case.latest_calendar_type.strCalTypeDescription }}</ValueCol>
              <ValueCol label="UP_BOND_DATE">{{ eoir_case.UP_BOND_DATE }}</ValueCol>
              <ValueCol label="UP_BOND_RSN">{{ eoir_case.UP_BOND_RSN }}</ValueCol>
              <ValueCol label="UPDATED_STATE">{{ eoir_case.UPDATED_STATE }}</ValueCol>
              <ValueCol label="ZBOND_MRG_FLAG">{{ eoir_case.ZBOND_MRG_FLAG }}</ValueCol>

              <ValueCol label="CORRECTIONAL_FAC">{{ eoir_case.CORRECTIONAL_FAC }}</ValueCol>
              <ValueCol label="RELEASE_MONTH">{{ eoir_case.RELEASE_MONTH }}</ValueCol>
              <ValueCol label="RELEASE_YEAR">{{ eoir_case.RELEASE_YEAR }}</ValueCol>
              <ValueCol label="INMATE_HOUSING">{{ eoir_case.INMATE_HOUSING }}</ValueCol>
              <ValueCol label="C_RELEASE_DATE">{{ eoir_case.C_RELEASE_DATE }}</ValueCol>
              <ValueCol label="DATE_DETAINED">{{ eoir_case.DATE_DETAINED }}</ValueCol>
              <ValueCol label="DATE_RELEASED">{{ eoir_case.DATE_RELEASED }}</ValueCol>
              <ValueCol label="DETENTION_DATE">{{ eoir_case.DETENTION_DATE }}</ValueCol>
              <ValueCol label="DETENTION_LOCATION">{{ eoir_case.DETENTION_LOCATION }}</ValueCol>
              <ValueCol label="DCO_LOCATION">{{ eoir_case.DCO_LOCATION }}</ValueCol>
              <ValueCol label="DETENTION_FACILITY_TYPE">{{ eoir_case.DETENTION_FACILITY_TYPE }}</ValueCol>
              <ValueCol label="LPR">{{ eoir_case.LPR }}</ValueCol>
            </v-row>
          </v-container>
        </ColapsibleCard>
      </v-col>
    </v-row>

    <v-row
      v-for="(proceeding, index) of eoir_case.proceedings"
      :key="proceeding.IDNPROCEEDING"
    >
      <v-col>
        <ColapsibleCard :title="`Proceeding #${proceeding.IDNPROCEEDING}`">
          <v-tabs v-model="selectedProceedingTab[index]"
            color="primary"
          >
            <v-tab value="proceeding">Proceeding</v-tab>
            <v-tab value="schedule">Schedule</v-tab>
          </v-tabs>
          <v-divider></v-divider>
          <v-tabs-window v-model="selectedProceedingTab[index]">
            <v-tabs-window-item value="proceeding">
              <v-container fluid>
                <v-row>
                  <ValueCol label="Sate of the Notice to Appear">{{ proceeding.OSC_DATE }}</ValueCol>
                  <ValueCol label="INPUT_DATE">{{ proceeding.INPUT_DATE }}</ValueCol>
                  <ValueCol label="BASE_CITY_CODE">{{ proceeding.base_city?.BASE_CITY_NAME }}</ValueCol>
                  <ValueCol label="HEARING_LOC_CODE">{{ proceeding.HEARING_LOC_CODE }}</ValueCol>
                  <ValueCol label="Judge">{{ proceeding.judge.JUDGE_NAME }}</ValueCol>
                  <ValueCol label="TRANS_IN_DATE">{{ proceeding.TRANS_IN_DATE }}</ValueCol>
                  <ValueCol label="PREV_HEARING_LOC">{{ proceeding.PREV_HEARING_LOC }}</ValueCol>
                  <ValueCol label="PREV_HEARING_BASE">{{ proceeding.PREV_HEARING_BASE }}</ValueCol>
                  <ValueCol label="PREV_IJ_CODE">{{ proceeding.PREV_IJ_CODE }}</ValueCol>
                  <ValueCol label="TRANS_NBR">{{ proceeding.TRANS_NBR }}</ValueCol>
                  <ValueCol label="HEARING_DATE">{{ proceeding.HEARING_DATE }}</ValueCol>
                  <ValueCol label="HEARING_TIME">{{ proceeding.HEARING_TIME }}</ValueCol>
                  <ValueCol label="DEC_TYPE">{{ proceeding.DEC_TYPE }}</ValueCol>
                  <ValueCol label="DEC_CODE">{{ proceeding.DEC_CODE }}</ValueCol>
                  <ValueCol label="DEPORTED_1">{{ proceeding.DEPORTED_1 }}</ValueCol>
                  <ValueCol label="DEPORTED_2">{{ proceeding.DEPORTED_2 }}</ValueCol>
                  <ValueCol label="OTHER_COMP">{{ proceeding.OTHER_COMP }}</ValueCol>
                  <ValueCol label="APPEAL_RSVD">{{ proceeding.APPEAL_RSVD }}</ValueCol>
                  <ValueCol label="APPEAL_NOT_FILED">{{ proceeding.APPEAL_NOT_FILED }}</ValueCol>
                  <ValueCol label="COMP_DATE">{{ proceeding.COMP_DATE }}</ValueCol>
                  <ValueCol label="In Absentia Hearing">{{ proceeding.ABSENTIA === "Y" ? "Yes" : "No" }}</ValueCol>
                  <ValueCol label="VENUE_CHG_GRANTED">{{ proceeding.VENUE_CHG_GRANTED }}</ValueCol>
                  <ValueCol label="TRANSFER_TO">{{ proceeding.TRANSFER_TO }}</ValueCol>
                  <ValueCol label="DATE_APPEAL_DUE_STATUS">{{ proceeding.DATE_APPEAL_DUE_STATUS }}</ValueCol>
                  <ValueCol label="TRANSFER_STATUS">{{ proceeding.TRANSFER_STATUS }}</ValueCol>
                  <ValueCol label="CUSTODY">{{ proceeding.CUSTODY }}</ValueCol>
                  <ValueCol label="CASE_TYPE">{{ proceeding.CASE_TYPE }}</ValueCol>
                  <ValueCol label="SCHEDULED_HEAR_LOC">{{ proceeding.SCHEDULED_HEAR_LOC }}</ValueCol>
                  <ValueCol label="CORRECTIONAL_FAC">{{ proceeding.CORRECTIONAL_FAC }}</ValueCol>
                  <ValueCol label="CRIM_IND">{{ proceeding.CRIM_IND }}</ValueCol>
                  <ValueCol label="IHP">{{ proceeding.IHP }}</ValueCol>
                  <ValueCol label="AGGRAVATE_FELON">{{ proceeding.AGGRAVATE_FELON }}</ValueCol>
                  <ValueCol label="DATE_DETAINED">{{ proceeding.DATE_DETAINED }}</ValueCol>
                  <ValueCol label="DATE_RELEASED">{{ proceeding.DATE_RELEASED }}</ValueCol>
                </v-row>
              </v-container>
            </v-tabs-window-item>
            <v-tabs-window-item value="schedule">
              <v-empty-state
                v-if="!proceeding.schedules.length"
                :icon="mdiCalendarQuestion"
                title="There are no entries"
                text="This Proceeding does not contain any Schedules to be shown."
              />
              <v-card
                v-for="schedule of proceeding.schedules"
                :key="schedule.IDNSCHEDULE"
                class="ma-6"
                variant="tonal"
              >
                <v-container fluid>
                  <v-row>
                    <ValueCol label="Schedule ID">{{ schedule.IDNSCHEDULE }}</ValueCol>
                    <ValueCol label="Date Scheduled">
                      {{ schedule.ADJ_DATE }} from {{ schedule.ADJ_TIME_START }} to {{ schedule.ADJ_TIME_STOP }}
                    </ValueCol>
                    <ValueCol label="Date of issuance of the NTA/OSC">{{ schedule.OSC_DATE }}</ValueCol>

                    <ValueCol label="Scheduled Hearing Type">{{ scheduledHearingTypeDescription(schedule.REC_TYPE) }}</ValueCol>
                    <ValueCol label="SCHEDULE_TYPE">{{ schedule.SCHEDULE_TYPE }}</ValueCol>

                    <ValueCol label="ADJ_MEDIUM">{{ scheduleMediumDescription(schedule.ADJ_MEDIUM) }}</ValueCol>
                    <ValueCol label="Court">
                      <p>{{ schedule.base_city.BASE_CITY_NAME }}</p>
                      <p>{{ schedule.base_city.BASE_ST_ADDRESS }}</p>
                      <p>{{ schedule.base_city.BASE_CITY }}</p>
                      <p>{{ schedule.base_city.BASE_STATE }}</p>
                    </ValueCol>
                    <ValueCol label="Hearing Location">
                      {{ schedule.hearing_location?.HEARING_CORR_TITLE }}<br>
                      {{ schedule.hearing_location?.HEARING_LOC_NAME }}<br>
                      {{ schedule.hearing_location?.HEARING_ST_ADDRESS }}<br>
                      {{ schedule.hearing_location?.HEARING_CITY }}<br>
                      {{ schedule.hearing_location?.HEARING_STATE }}<br>
                      {{ schedule.hearing_location?.HEARING_ZIP }}<br>
                      {{ schedule.hearing_location?.HEARING_PHONE_NO }}
                    </ValueCol>
                    <ValueCol label="Judge">{{ schedule.judge?.JUDGE_NAME }}</ValueCol>

                    <ValueCol label="Adjournment Reason">{{ schedule.adjournment?.strDesciption }}</ValueCol>
                    <ValueCol label="Adjournment Message">{{ schedule.ADJ_MSG }}</ValueCol>

                    <ValueCol label="INTERPRETER_CODE">{{ schedule.INTERPRETER_CODE }}</ValueCol>
                    <ValueCol label="Hearing Interpreted in">{{ schedule.language?.strDescription }}</ValueCol>

                    <ValueCol label="GENERATION">{{ schedule.GENERATION }}</ValueCol>
                    <ValueCol label="SUB_GENERATION">{{ schedule.SUB_GENERATION }}</ValueCol>
                    <ValueCol label="ASSIGNMENT_PATH">{{ schedule.ASSIGNMENT_PATH }}</ValueCol>
                    <ValueCol label="CAL_TYPE">{{ schedule.CAL_TYPE }}</ValueCol>
                    <ValueCol label="LNGSESSNID">{{ schedule.LNGSESSNID }}</ValueCol>
                    <ValueCol label="NOTICE_CODE">{{ schedule.NOTICE_CODE }}</ValueCol>
                    <ValueCol label="BLNCLOCKOVERRIDE">{{ schedule.BLNCLOCKOVERRIDE }}</ValueCol>
                    <ValueCol label="EOIRAttorneyID">{{ schedule.EOIRAttorneyID }}</ValueCol>

                    <ValueCol label="Created">{{ schedule.INPUT_DATE }} @ {{ schedule.INPUT_TIME }}</ValueCol>
                    <ValueCol label="Updated">{{ schedule.UPDATE_DATE }} @ {{ schedule.UPDATE_TIME }}</ValueCol>
                  </v-row>
                </v-container>
              </v-card>
            </v-tabs-window-item>
          </v-tabs-window>
        </ColapsibleCard>
      </v-col>
    </v-row>
  </v-container>
</template>
